import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { dailyTaskService } from '../../services/taskService';
import { siteService } from '../../services/siteService';
import { workerService } from '../../services/workerService';
import { companyService } from '../../services/companyService';
import { pdfExportService } from '../../services/pdfExportService';
import { TASK_CATEGORIES } from '../../data/taskData';
import KPICard from './components/KPICard';
import WorkerLeaderboard from './components/WorkerLeaderboard';
import SitePerformanceTable from './components/SitePerformanceTable';
import TrendChart from './components/TrendChart';
import CategoryBreakdown from './components/CategoryBreakdown';
import { useLanguage } from '../../contexts/LanguageContext';

const TaskAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [dateRange, setDateRange] = useState('week');
  const [selectedSite, setSelectedSite] = useState('all');
  const [activeSection, setActiveSection] = useState('overview');

  const [todayTasks, setTodayTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [sites, setSites] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const today = new Date()?.toISOString()?.split('T')?.[0];
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadWeekTasks();
  }, [dateRange]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [todayResult, sitesResult, workersResult, companyResult] = await Promise.all([
        dailyTaskService?.getForDateRange(sevenDaysAgo, today, null),
        siteService?.getAllSites(),
        workerService?.getAllWorkers(),
        companyService?.getUserCompany(),
      ]);
      setTodayTasks(todayResult?.data || []);
      setSites(sitesResult?.data || []);
      setWorkers((workersResult?.data || [])?.filter(w => w?.role === 'worker'));
      if (companyResult?.data) setCompanyName(companyResult?.data?.company_name || companyResult?.data?.name || '');
    } catch (e) {
      // non-critical
    } finally {
      setLoading(false);
    }
  };

  const loadWeekTasks = async () => {
    try {
      const days = dateRange === 'week' ? 7 : dateRange === 'month' ? 30 : 14;
      const startDate = new Date(Date.now() - days * 86400000)?.toISOString()?.split('T')?.[0];
      const result = await dailyTaskService?.getForDateRange(startDate, today, null);
      setAllTasks(result?.data || []);
    } catch (e) {
      // non-critical
    }
  };

  const filteredTodayTasks = useMemo(() => {
    if (selectedSite === 'all') return todayTasks;
    return todayTasks?.filter(t => t?.site_id === selectedSite);
  }, [todayTasks, selectedSite]);

  const kpis = useMemo(() => {
    const filtered = filteredTodayTasks;
    const total = filtered?.length;
    const completed = filtered?.filter(t => t?.status === 'completed')?.length;
    const blocked = filtered?.filter(t => t?.status === 'blocked')?.length;
    const required = filtered?.filter(t => t?.required);
    const requiredDone = required?.filter(t => t?.status === 'completed')?.length;
    const overdue = dailyTaskService?.getOverdueTasks(filtered)?.length;
    const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
    const requiredRate = required?.length > 0 ? Math.round((requiredDone / required?.length) * 100) : 100;
    return { total, completed, blocked, overdue, rate, requiredRate };
  }, [filteredTodayTasks]);

  const workerStats = useMemo(() => dailyTaskService?.groupByWorker(todayTasks, workers), [todayTasks, workers]);
  const siteStats = useMemo(() => dailyTaskService?.groupBySite(todayTasks, sites)?.filter(s => s?.tasks_total > 0), [todayTasks, sites]);

  // Build weekly stats from allTasks
  const weeklyStats = useMemo(() => {
    return workers?.map(worker => {
      const wTasks = allTasks?.filter(t => t?.assigned_to_user_id === worker?.id);
      const completed = wTasks?.filter(t => t?.status === 'completed')?.length;
      const rate = wTasks?.length > 0 ? Math.round((completed / wTasks?.length) * 100) : 0;
      return { ...worker, tasks_this_week: completed, tasks_this_month: completed, avg_completion_rate: rate };
    });
  }, [allTasks, workers]);

  const sitesNeedingAttention = siteStats?.filter(s => s?.completion_rate < 60 || s?.tasks_overdue > 0);

  const SECTIONS = [
    { id: 'overview', label: t('overview'), icon: 'LayoutDashboard' },
    { id: 'workers', label: t('workers'), icon: 'Users' },
    { id: 'sites', label: t('sites'), icon: 'Building2' },
    { id: 'trends', label: t('trends'), icon: 'TrendingUp' },
  ];

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleExportPDF = async () => {
    try {
      setExportLoading(true);
      const activeSite = selectedSite !== 'all' ? sites?.find(s => s?.id === selectedSite) : null;
      const result = await pdfExportService?.generateTaskAnalyticsReport({
        kpis,
        workerStats,
        siteStats,
        allTasks,
        siteName: activeSite?.site_name || 'All Sites',
        companyName,
        dateRange,
      });
      if (result?.error) throw result.error;
      showToast(`PDF exported: ${result?.filename}`, 'success');
    } catch (err) {
      console.error('[TaskAnalyticsDashboard] export PDF failed:', err);
      showToast('Failed to export PDF. Please try again.', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Task Analytics — SiteProof</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          {/* Toast */}
          {toast && (
            <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast?.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}>
              <Icon name={toast?.type === 'success' ? 'CheckCircle2' : 'AlertCircle'} size={16} />
              {toast?.message}
            </div>
          )}

          {/* Page Header */}
          <div className="bg-card border-b border-border">
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
              <div className="flex items-center justify-between py-5">
                <div>
                  <h1 className="text-2xl font-bold text-foreground font-heading">{t('taskAnalytics')}</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">{t('taskAnalyticsSubtitle')}</p>
                </div>
                <div className="flex items-center gap-3">
                  <select value={selectedSite} onChange={e => setSelectedSite(e?.target?.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent">
                    <option value="all">{t('allSites')}</option>
                    {sites?.map(s => <option key={s?.id} value={s?.id}>{s?.site_name}</option>)}
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName={exportLoading ? 'Loader2' : 'Download'}
                    onClick={handleExportPDF}
                    disabled={exportLoading || loading}
                  >
                    {exportLoading ? t('exporting') : t('exportPdf')}
                  </Button>
                  <Button variant="primary" size="sm" iconName="ClipboardList" iconPosition="left" onClick={() => navigate('/task-planner')}>{t('planTasks')}</Button>
                </div>
              </div>
              {/* Section Tabs */}
              <div className="flex items-center gap-1 overflow-x-auto pb-px scrollbar-hide">
                {SECTIONS?.map(s => (
                  <button key={s?.id} onClick={() => setActiveSection(s?.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeSection === s?.id ? 'border-accent text-accent' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                    <Icon name={s?.icon} size={15} />{s?.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 space-y-6">

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
              </div>
            ) : (
              <>
                {/* Sites Needing Attention Banner */}
                {sitesNeedingAttention?.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3">
                    <Icon name="AlertTriangle" size={18} className="text-orange-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-orange-800">{sitesNeedingAttention?.length} {sitesNeedingAttention?.length !== 1 ? t('sitesNeedAttention') : t('siteNeedsAttention')}</p>
                      <p className="text-xs text-orange-600">{sitesNeedingAttention?.map(s => s?.name || s?.site_name)?.join(', ')}</p>
                    </div>
                    <button onClick={() => setActiveSection('sites')} className="text-xs font-semibold text-orange-700 hover:underline flex items-center gap-1">{t('view')} <Icon name="ArrowRight" size={12} /></button>
                  </div>
                )}

                {/* OVERVIEW */}
                {activeSection === 'overview' && (
                  <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                      <KPICard label={t('totalToday')} value={kpis?.total} icon="ClipboardList" color="text-foreground" bg="bg-card" onClick={() => navigate('/task-completion-workflow')} />
                      <KPICard label={t('completed')} value={kpis?.completed} icon="CheckCircle2" color="text-emerald-600" bg="bg-emerald-50" onClick={() => navigate('/task-completion-workflow?status=completed')} />
                      <KPICard label={t('overdue')} value={kpis?.overdue} icon="AlertCircle" color="text-orange-600" bg="bg-orange-50" onClick={() => navigate('/task-completion-workflow?status=overdue')} />
                      <KPICard label={t('blocked')} value={kpis?.blocked} icon="XCircle" color="text-red-600" bg="bg-red-50" onClick={() => navigate('/task-completion-workflow?status=blocked')} />
                      <KPICard label={t('completionRate')} value={`${kpis?.rate}%`} icon="TrendingUp" color={kpis?.rate >= 80 ? 'text-emerald-600' : kpis?.rate >= 50 ? 'text-amber-600' : 'text-red-600'} bg="bg-accent/5" onClick={() => {}} />
                      <KPICard label={t('requiredRate')} value={`${kpis?.requiredRate}%`} icon="ShieldCheck" color={kpis?.requiredRate >= 90 ? 'text-emerald-600' : 'text-red-600'} bg={kpis?.requiredRate >= 90 ? 'bg-emerald-50' : 'bg-red-50'} onClick={() => {}} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Trend Chart */}
                      <div className="lg:col-span-2">
                        <TrendChart tasks={allTasks} />
                      </div>
                      {/* Category Breakdown */}
                      <div>
                        <CategoryBreakdown tasks={filteredTodayTasks} />
                      </div>
                    </div>

                    {/* Site Performance Summary */}
                    <SitePerformanceTable siteStats={siteStats} onSiteClick={(siteId) => navigate(`/task-completion-workflow?site=${siteId}`)} />
                  </>
                )}

                {/* WORKERS */}
                {activeSection === 'workers' && (
                  <WorkerLeaderboard workerStats={workerStats} weeklyStats={weeklyStats} onWorkerClick={(wId) => navigate(`/task-completion-workflow?worker=${wId}`)} />
                )}

                {/* SITES */}
                {activeSection === 'sites' && (
                  <SitePerformanceTable siteStats={siteStats} onSiteClick={(siteId) => navigate(`/task-completion-workflow?site=${siteId}`)} detailed />
                )}

                {/* TRENDS */}
                {activeSection === 'trends' && (
                  <div className="space-y-6">
                    <TrendChart tasks={allTasks} expanded />
                    <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
                      <div className="px-5 py-4 border-b border-border">
                        <h3 className="font-semibold text-foreground font-heading">{t('categoryPerformance')}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{t('categoryPerformanceSubtitle')}</p>
                      </div>
                      <div className="p-5">
                        <div className="space-y-4">
                          {TASK_CATEGORIES?.map(cat => {
                            const catTasks = filteredTodayTasks?.filter(t => t?.category === cat);
                            const done = catTasks?.filter(t => t?.status === 'completed')?.length;
                            const rate = catTasks?.length > 0 ? Math.round((done / catTasks?.length) * 100) : 0;
                            const colors = { Safety: 'bg-red-500', Quality: 'bg-blue-500', Operations: 'bg-amber-500', Documentation: 'bg-purple-500' };
                            return (
                              <div key={cat}>
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-2.5 h-2.5 rounded-full ${colors?.[cat]}`} />
                                    <span className="text-sm font-medium text-foreground">{cat}</span>
                                    <span className="text-xs text-muted-foreground">{done}/{catTasks?.length}</span>
                                  </div>
                                  <span className={`text-sm font-bold ${rate >= 80 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{rate}%</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                                  <div className={`h-full rounded-full transition-all duration-500 ${colors?.[cat]}`} style={{ width: `${rate}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default TaskAnalyticsDashboard;
